import { useState, useCallback } from 'react';
import type { QuestionnaireTemplate } from '../types/types';
import { supabase } from '../lib/supabase';

export function useQuestionnaireTemplates() {
  const [organisationTemplates, setOrganisationTemplates] = useState<
    QuestionnaireTemplate[]
  >([]);
  const [defaultTemplates, setDefaultTemplates] = useState<
    QuestionnaireTemplate[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch templates for a specific organisation
  const getOrganisationTemplates = useCallback(
    async (organisationId: string | undefined) => {
      if (!organisationId) return;

      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('questionnaire_templates')
        .select('*')
        .eq('organisation_id', organisationId)
        .order('created_at', { ascending: false });

      if (error) {
        setError(error);
      } else if (data) {
        setOrganisationTemplates(data);
      }

      setLoading(false);
    },
    []
  );

  // Fetch default templates (is_default = true, ignore organisationId)
  const getDefaultTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error } = await supabase
      .from('questionnaire_templates')
      .select('*, questions(id)')
      .eq('is_default', true)
      .eq('status', 'published')
      .order('created_at', { ascending: false });

    if (error) {
      setError(error);
    } else if (data) {
      const templatesWithQuestionCount = data.map((template) => ({
        ...template,
        questionCount: template.questions?.length || 0,
      }));

      setDefaultTemplates(templatesWithQuestionCount);
    }

    setLoading(false);
  }, []);

  // ✅ Fetch a template by assessment_id
  const getTemplateByAssessmentId = useCallback(
    async (assessmentId: string) => {
      if (!assessmentId) return null;

      setLoading(true);
      setError(null);

      const { data: assessment, error: assessmentError } = await supabase
        .from('assessments')
        .select('template_id, status, supplier_id, organisation_id')
        .eq('id', assessmentId)
        .single();

      if (assessmentError) {
        setError(assessmentError);
        setLoading(false);
        return null;
      }

      if (!assessment?.template_id) {
        setError(new Error('Assessment has no template_id'));
        setLoading(false);
        return null;
      }

      const { data: template, error: templateError } = await supabase
        .from('questionnaire_templates')
        .select('*')
        .eq('id', assessment.template_id)
        .single();

      if (templateError) {
        setError(templateError);
        setLoading(false);
        return null;
      }

      setLoading(false);
      return {
        ...template,
        assessmentStatus: assessment?.status,
        supplierId: assessment?.supplier_id,
        organisationId: assessment?.organisation_id,
      } as QuestionnaireTemplate;
    },
    []
  );

  // ✅ Fetch a template by template_id
  const getTemplateByTemplateId = useCallback(async (templateId: string) => {
    if (!templateId) return null;

    setLoading(true);
    setError(null);

    const { data, error } = await supabase
      .from('questionnaire_templates')
      .select('*')
      .eq('id', templateId)
      .single();

    if (error) {
      setError(error);
      setLoading(false);
      return null;
    }

    setLoading(false);
    return data as QuestionnaireTemplate;
  }, []);

  return {
    organisationTemplates,
    defaultTemplates,
    loading,
    error,
    getOrganisationTemplates,
    getDefaultTemplates,
    getTemplateByAssessmentId,
    getTemplateByTemplateId, // 👈 added here
  };
}
